<template>
    <DropdownMenu :position-y-offset="8" :width="164" :has-no-padding="true" :has-shadow="true" dropdown-class="language-select-dropdown">
        <template #activator="{ isOpen }">
            <button
                type="button"
                class="language-select"
                :class="{ open: isOpen }"
                :title="$t('Select your language')"
                aria-haspopup="menu"
                :aria-expanded="isOpen ? 'true' : 'false'"
            >
                <img class="language-select-flag" :src="currentLanguage.flagUrl" :alt="currentLanguage.flagAlt" />
                <span class="language-select-label">{{ currentLanguage.label }}</span>
                <i class="fas fa-chevron-down language-select-chevron" aria-hidden="true"></i>
            </button>
        </template>

        <template #items="{ onClose }">
            <div class="language-select-options" role="menu" :aria-label="$t('Select your language')">
                <button
                    v-for="language in languages"
                    :key="language.id"
                    type="button"
                    class="language-select-option"
                    :class="{ selected: language.id === currentLanguage.id }"
                    role="menuitemradio"
                    :aria-checked="language.id === currentLanguage.id ? 'true' : 'false'"
                    @click="changeLanguage(language.id, onClose)"
                >
                    <img class="language-select-flag" :src="language.flagUrl" :alt="language.flagAlt" />
                    <span class="language-select-option-label">{{ language.label }}</span>
                    <i v-if="language.id === currentLanguage.id" class="fas fa-check language-select-check" aria-hidden="true"></i>
                </button>
            </div>
        </template>
    </DropdownMenu>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { AVAILABLE_LANGUAGES, getLanguage, setLanguage } from "@/i18n";
import DropdownMenu from "@/components/dropdowns/dropdown-menu/DropdownMenu.vue";

type LanguageOption = {
    id: string;
    label: string;
    flagUrl: string;
    flagAlt: string;
};

const FLAG_URLS: Record<string, string> = {
    en: new URL("../../../assets/svg/flags/flag-gb.svg", import.meta.url).href,
    es: new URL("../../../assets/svg/flags/flag-es.svg", import.meta.url).href,
};

const LANGUAGE_LABELS: Record<string, Record<string, string>> = {
    en: {
        en: "English",
        es: "Spanish",
    },
    es: {
        en: "Inglés",
        es: "Español",
    },
};

const LANGUAGE_FLAG_ALTS: Record<string, string> = {
    en: "English",
    es: "Spanish",
};

export default defineComponent({
    components: {
        DropdownMenu,
    },
    name: "LanguageSelectMenu",
    data() {
        return {
            selectedLanguageId: getLanguage(),
        };
    },
    computed: {
        displayLocale: function (): string {
            return (this.$locale.value || this.selectedLanguageId || getLanguage()).split("-")[0];
        },

        languages: function (): LanguageOption[] {
            const languageLabels = LANGUAGE_LABELS[this.displayLocale] || LANGUAGE_LABELS.en;

            return AVAILABLE_LANGUAGES.map((language) => ({
                id: language.id,
                label: languageLabels[language.id] || language.name,
                flagUrl: FLAG_URLS[language.id] || FLAG_URLS.en,
                flagAlt: LANGUAGE_FLAG_ALTS[language.id] || language.name,
            }));
        },

        currentLanguage: function (): LanguageOption {
            const currentLanguageId = this.$locale.value || this.selectedLanguageId || getLanguage();
            return this.languages.find((language) => language.id === currentLanguageId) || this.languages[0];
        },
    },
    methods: {
        changeLanguage: function (languageId: string, onClose: () => void) {
            this.selectedLanguageId = languageId;
            setLanguage(languageId);
            onClose();
        },
    },
});
</script>

<style scoped>
.language-select {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 164px;
    height: 38px;
    padding: 0 10px;
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    background: var(--color-background-surface);
    color: var(--color-text-default);
    cursor: pointer;
    font: inherit;
    line-height: 1;
}

.language-select:hover,
.language-select.open {
    background: var(--color-background-neutral-hover-subtle);
}

.language-select:focus-visible {
    outline: 2px solid var(--color-background-primary-brand-default);
    outline-offset: 2px;
}

.language-select-flag {
    width: 22px;
    height: 20px;
    flex: 0 0 auto;
    border-radius: 3px;
    object-fit: cover;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.language-select-label,
.language-select-option-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-text-default);
}

.language-select-label {
    flex: 1;
    text-align: left;
}

.language-select-chevron {
    flex: 0 0 auto;
    font-size: 12px;
    color: var(--color-icon-neutral-subtle);
    transition: transform 160ms ease;
}

.language-select.open .language-select-chevron {
    transform: rotate(180deg);
}

.language-select-options {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 2px;
}

.language-select-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 34px;
    padding: 6px 4px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--color-text-default);
    cursor: pointer;
    font: inherit;
    text-align: left;
}

.language-select-option:hover,
.language-select-option:focus-visible {
    background: var(--color-background-neutral-hover-subtle);
}

.language-select-option:focus-visible {
    outline: 2px solid var(--color-background-primary-brand-default);
    outline-offset: -2px;
}

.language-select-option-label {
    flex: 1;
}

.language-select-option.selected .language-select-option-label {
    font-weight: 600;
}

.language-select-check {
    flex: 0 0 auto;
    font-size: 13px;
    color: var(--color-icon-info);
}
</style>
